import { NgClass } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { Helpers } from '../../../../../utilities/helpers';
import { IAcademicYears } from '../../../../../interfaces/academic-year';
import { IAcademicTerms } from '../../../../../interfaces/academic-term';
import {
  IStudentImportPreview,
  IStudentImportResult,
} from '../../../../../interfaces/student-import';
import { AcademicTermService } from '../../../../../services/academic-term';
import { AcademicYearService } from '../../../../../services/academic-year';
import { ExportService } from '../../../../../services/export';
import { FlashService } from '../../../../../services/flash';
import { StudentImportService } from '../../../../../services/student-import';

@Component({
  selector: 'app-student-import',
  imports: [FormsModule, NgClass, LoadingIndicator, NotFoundError],
  templateUrl: './student-import.html',
  styleUrl: './student-import.css',
})
export class StudentImport {
  constructor(
    private academicYearService: AcademicYearService,
    private academicTermService: AcademicTermService,
    private studentImportService: StudentImportService,
    private exportService: ExportService,
  ) {
    this.getAcademicYears();
  }

  protected readonly flashService = inject(FlashService);
  protected readonly Helpers = Helpers;
  private readonly templateUrl = `${ApiEndpoints.url}/student/import/template`;
  protected readonly allowedExtensions = ['.xlsx', '.xlsm'];
  protected readonly maxFileSizeBytes = 5 * 1024 * 1024;
  protected academicYears = signal<IAcademicYears | null>(null);
  protected terms = signal<IAcademicTerms | null>(null);
  protected academicYearId = '';
  protected termId = '';
  protected selectedFile = signal<File | null>(null);
  protected preview = signal<IStudentImportPreview | null>(null);
  protected result = signal<IStudentImportResult | null>(null);
  protected isDownloadingTemplate = signal<boolean>(false);
  protected isUploading = signal<boolean>(false);
  protected isConfirming = signal<boolean>(false);
  protected isLoadingTerms = signal<boolean>(false);
  protected showConfirm = signal<boolean>(false);

  protected getAcademicYears(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 100,
    };

    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.academicYears.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load academic years. Refresh the page.',
          );
        },
      });
  }

  protected getAcademicTerms(academicYearId: string): void {
    if (!academicYearId) {
      this.terms.set(null);
      return;
    }

    this.isLoadingTerms.set(true);

    const params: Record<string, string | number> = {
      page: 0,
      size: 100,
      academic_year_id: academicYearId,
    };

    this.academicTermService
      .getAcademicTerms(`${ApiEndpoints.url}/academic-term`, params)
      .subscribe({
        next: (res) => {
          this.terms.set(res);
          this.isLoadingTerms.set(false);
        },
        error: (err) => {
          this.isLoadingTerms.set(false);
          this.terms.set(null);
          this.flashService.failure(
            err?.error?.message ?? 'Could not load academic terms. Refresh the page.',
          );
        },
      });
  }

  protected onAcademicYearChange(academicYearId: string): void {
    this.academicYearId = academicYearId;
    this.termId = '';
    this.resetPreview();
    this.getAcademicTerms(academicYearId);
  }

  protected onTermChange(termId: string): void {
    this.termId = termId;
    this.resetPreview();
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;

    this.resetPreview();

    if (!file) {
      this.selectedFile.set(null);
      return;
    }

    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!this.allowedExtensions.includes(extension)) {
      input.value = '';
      this.selectedFile.set(null);
      this.flashService.failure('Only .xlsx or .xlsm Excel files are supported.');
      return;
    }

    if (file.size > this.maxFileSizeBytes) {
      input.value = '';
      this.selectedFile.set(null);
      this.flashService.failure('The file is larger than the 5 MB limit.');
      return;
    }

    this.selectedFile.set(file);
  }

  protected downloadTemplate(): void {
    this.isDownloadingTemplate.set(true);
    this.exportService.export(this.templateUrl, 'student-import-template.xlsx');
    this.isDownloadingTemplate.set(false);
  }

  protected uploadFile(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Select an academic year and an Excel file.');
      return;
    }

    const file = this.selectedFile();

    if (!file) {
      this.flashService.failure('Select an Excel file to upload.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('academic_year_id', this.academicYearId);

    if (this.termId) {
      formData.append('term_id', this.termId);
    }

    this.isUploading.set(true);
    this.isDownloadingTemplate.set(false);

    this.studentImportService
      .previewImport(`${ApiEndpoints.url}/student/import/preview`, formData)
      .subscribe({
        next: (res) => {
          this.preview.set(res);
          this.isUploading.set(false);

          if (!res.payload.summary.valid) {
            this.flashService.failure('No valid rows were found. Review the issues below.');
          } else {
            this.flashService.success('File validated successfully.');
          }
        },
        error: (err) => {
          this.isUploading.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not validate the file. Try again.',
          );
        },
      });
  }

  protected openConfirm(): void {
    const preview = this.preview();

    if (!preview || !preview.payload.summary.valid) {
      return;
    }

    this.showConfirm.set(true);
  }

  protected confirmImport(): void {
    const preview = this.preview();

    if (!preview) {
      return;
    }

    this.isConfirming.set(true);

    this.studentImportService
      .confirmImport(`${ApiEndpoints.url}/student/import/confirm`, preview.payload.batch.id)
      .subscribe({
        next: (res) => {
          this.result.set(res);
          this.isConfirming.set(false);
          this.showConfirm.set(false);
          this.flashService.success(res.message || 'Import completed.');
        },
        error: (err) => {
          this.isConfirming.set(false);
          this.showConfirm.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not complete the import. Try again.',
          );
        },
      });
  }

  protected downloadErrors(): void {
    const batchId = this.result()?.payload.batch_id ?? this.preview()?.payload.batch.id;

    if (!batchId) {
      return;
    }

    this.exportService.export(
      `${ApiEndpoints.url}/student/import/${batchId}/errors`,
      `student-import-errors.xlsx`,
    );
  }

  protected resetPreview(): void {
    this.preview.set(null);
    this.result.set(null);
    this.showConfirm.set(false);
  }

  protected clearUpload(): void {
    this.resetPreview();
    this.selectedFile.set(null);
  }

  protected fileSizeLabel(bytes: number): string {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  protected isConfirmed(): boolean {
    return this.result() !== null;
  }

  protected trackRow(index: number): number {
    return index;
  }
}
